import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'

interface Agent {
  id: string
  name: string
  model: { id: string; speed?: string } | string
  version: number
  description: string | null
  system: string | null
  tools: Array<{ type: string }>
  mcp_servers: Array<{ name: string; type: string }>
  skills: Array<{ type: string }>
  metadata: Record<string, string>
  created_at: string
  updated_at: string
  archived_at: string | null
}

interface Props {
  agents: Agent[]
  tab: string
  api_error?: string
  flash?: { success?: string; error?: string }
}

function modelId(model: Agent['model']): string {
  if (typeof model === 'string') return model
  return model?.id || 'unknown'
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function Index({ agents, api_error }: Props) {
  const { t } = useTranslation('admin')
  const [archiving, setArchiving] = useState<string | null>(null)

  const handleArchive = (agentId: string, agentName: string) => {
    if (!confirm(`Archive agent "${agentName}"? New sessions cannot reference it, but existing sessions continue.`)) return
    setArchiving(agentId)
    router.post(`/admin/managed_agents/${agentId}/archive`, {}, {
      onFinish: () => setArchiving(null),
    })
  }

  const activeAgents = agents.filter(a => !a.archived_at)
  const archivedAgents = agents.filter(a => a.archived_at)
  const sortedAgents = [...activeAgents, ...archivedAgents]

  return (
    <>
      <AppHeader breadcrumbs={[{ label: `Managed Agents (${agents.length})` }]} />

      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="default" size="sm" disabled>Agents</Button>
          <Link href="/admin/managed_agents/environments">
            <Button variant="outline" size="sm">Environments</Button>
          </Link>
        </div>

        {api_error && (
          <Card className="mb-4 border-red-200 dark:border-red-800">
            <CardContent className="py-3 text-sm text-red-600 dark:text-red-400">
              API Error: {api_error}
            </CardContent>
          </Card>
        )}

        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Model</th>
                <th className="text-left px-4 py-3 font-medium">Version</th>
                <th className="text-left px-4 py-3 font-medium">Tools</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Created</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAgents.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    {api_error ? 'Could not load agents' : 'No agents found'}
                  </td>
                </tr>
              )}
              {sortedAgents.map(agent => (
                <tr key={agent.id} className="border-b border-border hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link href={`/admin/managed_agents/${agent.id}`} className="font-medium text-foreground hover:underline">
                      {agent.name}
                    </Link>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">{agent.id}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{modelId(agent.model)}</td>
                  <td className="px-4 py-3 font-mono">v{agent.version}</td>
                  <td className="px-4 py-3 text-muted-foreground">{agent.tools?.length || 0}</td>
                  <td className="px-4 py-3">
                    {agent.archived_at ? (
                      <Badge variant="secondary">Archived</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(agent.created_at)}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Link href={`/admin/managed_agents/${agent.id}`} className="text-blue-600 hover:underline text-xs">
                      View
                    </Link>
                    {!agent.archived_at && (
                      <button
                        onClick={() => handleArchive(agent.id, agent.name)}
                        disabled={archiving === agent.id}
                        className="text-amber-600 hover:underline text-xs disabled:opacity-50"
                      >
                        {archiving === agent.id ? 'Archiving...' : 'Archive'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
